import { Object3D } from "three";
import { Visual } from "../models/Visual";
import { BaseVisual } from "./BaseVisual";
import { ModelLoader } from "../panels/common/ModelLoader";
import { BaseAnimator } from "./BaseAnimator";
import { GLBAnimator } from "./GLBAnimator";
import { addRotation, copyToEuler, copyVec3, isNotZero } from "./visual-utils";
import { CollisionBox } from "../models/Model";
import * as THREE from "three";

export interface CameraConfig {
  position: THREE.Vector3;
  rotation: THREE.Euler;
  gimbal?: THREE.Euler;
  fov: number;
  parent: THREE.Object3D;
  name: string;
}

export interface ModelBased extends Visual {
  modelId: string;
  floorId: string;
}
export interface VisualVelocity {
  angular:THREE.Vector3;
  linear:THREE.Vector3;
}

export interface IAgent {
  render(delta: number): boolean;
}

export class ModelBasedVisual extends BaseVisual implements ModelBased {
  modelId: string = '';
  public floorId: string = '';
  modelLoader = new ModelLoader();
  animator!:BaseAnimator;

  public getType(): string {
    return 'ModelBasedVisual';
  }
  
  protected async buildObj(): Promise<Object3D> {
    const group = new THREE.Group();
    const model = await this.wr.modelService.getModel(this.modelId);
    await this.modelLoader.loadModel(model);
    this.animator = this.createAnimator();
    group.add(this.modelLoader.getContainer());
    return group;
  }

  public convertToLocal(point: THREE.Vector3): THREE.Vector3 {
    if (this.siteAnchorId) {
      const siteAnchor = this.wr.dataService.activeSite;
      if (siteAnchor) {
        return siteAnchor.convertToLocal(point);
      }
    }
    return this.obj.worldToLocal(point.clone());
  }

  public render(delta: number): void {
    if (this.animator) {
      this.animator.render(delta);
    }
    if (this.agent && this.agent.render(delta)) {
      return;
    }
    if (isNotZero(this.velocity.linear)) {
      // Handle linear velocity
      const move = this.velocity.linear.clone().multiplyScalar(delta);
      this.obj.position.add(move);
      copyVec3(this.obj.position, this.position);
    }
    if (isNotZero(this.velocity.angular)) {
      addRotation(this.obj, this.velocity.angular, delta);
      copyVec3(this.obj.rotation, this.rotation);
    }
  }

  public updateObj(): void {
    this.obj.position.copy(this.position);
    copyToEuler(this.rotation, this.obj.rotation);
  }

  public getFrindlyName(): string {
    return this.id || this.modelLoader.getModelName();
  }

  public createAnimator(): BaseAnimator {
    return new GLBAnimator(this.modelLoader);
  }

  protected getCollisionBoxes(): CollisionBox[] {
    return this.modelLoader.model?.collisionMap.boxes || [];
  }

  public createDataTemplate(): Visual {
    const data: ModelBased = {
      type: this.getType(),
      _id: this._id,
      id: this.id,
      position: this.position,
      rotation: this.rotation,
      orgId: this.orgId,
      siteAnchorId: this.siteAnchorId,
      modelId: this.modelId,
      location: this.location,
      floorId: this.floorId,
    };
    return data;
  }

  public getCameras(): CameraConfig[] {
    return [];
  }
}